import type { Logger } from '@zg-rentals/logger-node';

/**
 *  WebSocketWrapper provides a wrapper to a WebSocket connection.
 *  The WebSocket instance can be accessed via the 'ws' property.
 *
 *  Currently, WebSocketWrapper provides the following functionality:
 *      - Attempts to reconnect if the connection closes without a 1000 code (clean close)
 *
 *
 *  const mySocket = new WebSocketWrapper('ws://someurl.com');
 *  const readyState = mySocket.ws.readyState;
 *
 *  Both WebSocket methods, send and close, can be accessed by the WebSocketWrapper helpers
 *  mySocket.send('some message');
 *  mySocket.close();
 *
 *
 */
const MIN_RECONNECT_INTERVAL = 50;
const MAX_RECONNECT_INTERVAL = 60000;
const RECONNECT_DECAY_FACTOR = 2;
const CONNECTION_TIMEOUT = 1000;
type WebSocketMessageData = string | Blob | BufferSource;

export interface WebSocketWrapperOptions {
  minReconnect?: number;
  maxReconnect?: number;
  reconnectDecay?: number;
  timeout?: number;
  url: string;
  protocols?: string | Array<string>;
  logger?: Logger;
  handlers?: Partial<WebSocketHandlers>;
}

export type WebsocketHandler<K extends keyof WebSocketEventMap> = (this: WebSocket, ev: WebSocketEventMap[K]) => void;

type WebSocketHandlers = { [K in keyof WebSocketEventMap]: Array<WebsocketHandler<K>> };

interface WebSocketEvent extends Event {
  reason: string;
  code: number;
}

export class WebSocketWrapper {
  private url: string;
  private protocols: string | Array<string> | undefined;
  private minReconnect: number;
  private maxReconnect: number;
  private reconnectDecay: number;
  private connectionTimeout: number;
  private reconnectInterval: number = MIN_RECONNECT_INTERVAL;
  // @ts-ignore TS wants these timers to be initialized here, but we don't.
  private createConnectionTimer: ReturnType<typeof setTimeout>;
  // @ts-ignore TS wants these timers to be initialized here, but we don't.
  private reconnectTimer: ReturnType<typeof setTimeout>;
  private numOfAttempts: number;
  private logger?: Logger;
  private messageQueue: Array<WebSocketMessageData> = [];
  public handlers: WebSocketHandlers;
  public ws: WebSocket | undefined;

  /**
   *
   * @param url - The URL to which to connect; this should be the URL to which the WebSocket server will respond.
   * @param protocols (optional) - Either a single protocol string or an array of protocol strings.
   *  These strings are used to indicate sub-protocols, so that a single server can implement multiple WebSocket sub-protocols
   *  (for example, you might want one server to be able to handle different types of interactions depending on the specified
   *  protocol). If you don't specify a protocol string, an empty string is assumed.
   *
   * @param {WebSocketWrapper.Options.minReconnect} number (optional) - Time in ms that the websocket will wait to reconnect in its first attempt
   * @param {WebSocketWrapper.Options.maxReconnect} number (optional) - Time in ms that the websocket will wait to reconnect at most
   * @param {WebSocketWrapper.Options.reconnectDecay} number (optional) - Factor by which the next reconnect time will try again
   * @param {WebSocketWrapper.Options.timeout} number (optional) - Time in ms for establishing a new connection before a reconnect timer is started
   */
  constructor(options: WebSocketWrapperOptions) {
    const { url, protocols, minReconnect, maxReconnect, reconnectDecay, timeout, logger, handlers } = options;

    this.url = url;
    this.protocols = protocols;
    this.minReconnect = minReconnect || MIN_RECONNECT_INTERVAL;
    this.maxReconnect = maxReconnect || MAX_RECONNECT_INTERVAL;
    this.reconnectDecay = reconnectDecay || RECONNECT_DECAY_FACTOR;
    this.connectionTimeout = timeout || CONNECTION_TIMEOUT;
    this.logger = logger;
    this.handlers = {
      open: (handlers && handlers.open) || [],
      close: (handlers && handlers.close) || [],
      message: (handlers && handlers.message) || [],
      error: (handlers && handlers.error) || [],
    };
    this.numOfAttempts = 0;

    this.createConnection();
  }

  send(data: WebSocketMessageData): void {
    if (!this.ws) {
      this.messageQueue.push(data);
      this.logger?.info('WebSocketWrapper: Cannot send data - WebSocket is not initialized. Message queued.');
      return;
    }

    if (this.ws.readyState !== WebSocket.OPEN) {
      this.messageQueue.push(data);
      this.logger?.info(
        'WebSocketWrapper: Cannot send data - WebSocket is not open. ReadyState: %d. Message queued.',
        this.ws.readyState,
      );
      return;
    }

    return this.ws.send(data);
  }

  close(code?: number, reason?: string): void {
    return this.ws?.close(code, reason);
  }

  addEventListener<K extends keyof WebSocketEventMap>(type: K, listener: WebsocketHandler<K>) {
    this.logger?.info('add event listener', type, listener);
    this.attachHandler(type, listener);

    if (this.ws) {
      this.ws.addEventListener(type, listener);
    }
  }

  removeEventListener<K extends keyof WebSocketEventMap>(type: K, listener: WebsocketHandler<K>) {
    this.removeHandler(type, listener);

    if (this.ws) {
      this.ws.removeEventListener(type, listener);
    }
  }

  attachHandler<K extends keyof WebSocketEventMap>(type: K, listener: WebsocketHandler<K>) {
    this.handlers = {
      ...this.handlers,
      [type]: [...this.handlers[type], listener],
    };
  }

  removeHandler<K extends keyof WebSocketEventMap>(type: K, listener: WebsocketHandler<K>) {
    this.handlers = {
      ...this.handlers,
      [type]: this.handlers[type].filter((handler) => handler !== listener),
    };
    this.logger?.info('removed old handlers', listener);
  }

  createConnection = () => {
    this.logger?.info('WebSocketWrapper creating new WebSocket connection attempt=%s', this.numOfAttempts);

    this.numOfAttempts++;

    this.ws = new WebSocket(this.url, this.protocols);

    this.ws.addEventListener('open', this.handleOnOpen.bind(this));
    this.ws.addEventListener('close', this.handleOnClose.bind(this));

    // re attach handlers
    Object.keys(this.handlers).forEach((type) => {
      this.handlers[type as keyof WebSocketEventMap].forEach((handler) =>
        this.ws?.addEventListener(
          type as keyof WebSocketEventMap,
          handler as WebsocketHandler<keyof WebSocketEventMap>,
        ),
      );
    });

    // Give the WebSocket some time to connect
    this.reconnectTimer = setTimeout(() => this.reconnect(this.ws), this.connectionTimeout);
  };

  handleOnOpen = () => {
    this.logger?.info('WebSocketWrapper has opened WebSocket connection');

    this.processQueue();
    this.resetReconnectInterval();
  };

  handleOnClose = (event: WebSocketEvent) => {
    this.logger?.info('WebSocketWrapper closed WebSocket connection');

    if (event.code !== 1000) {
      this.logger?.info('WebSocketWrapper did not close normally. Code: %d, Reason: %s', event.code, event.reason);

      // WebSocket did not close normally. Try to reconnect.
      if (this.ws) {
        this.ws.removeEventListener('open', this.handleOnOpen);
        this.ws.removeEventListener('close', this.handleOnClose);
        this.ws = undefined;

        this.reconnect();
      }
    }
  };

  setNextReconnectInterval = () => {
    const newTime = this.reconnectInterval * this.reconnectDecay;

    if (this.reconnectInterval === this.maxReconnect) {
      // Already at max, nothing to do
      return;
    }

    if (newTime >= this.maxReconnect) {
      this.reconnectInterval = this.maxReconnect;
    } else {
      this.reconnectInterval = newTime;
    }
  };

  reconnect = (ws?: WebSocket) => {
    if (!ws) {
      this.logger?.info('WebSocketWrapper retrying connection in: %O ms', this.reconnectInterval);

      this.setNextReconnectInterval();

      this.createConnectionTimer = setTimeout(() => this.createConnection(), this.reconnectInterval);

      return;
    }
  };

  resetReconnectInterval() {
    this.numOfAttempts = 0;
    clearTimeout(this.createConnectionTimer);
    clearTimeout(this.reconnectTimer);
    this.reconnectInterval = this.minReconnect;
  }

  processQueue() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const queueSize = this.messageQueue.length;
      if (queueSize > 0) {
        this.logger?.info('WebSocketWrapper: Processing %d queued messages', queueSize);
      }

      while (this.messageQueue.length > 0) {
        const message = this.messageQueue.shift();
        if (message) {
          this.ws.send(message);
        }
      }
    }
  }
}
